{
  "chapitres": [
    {
      "id": "esprit",
      "titre": "L’esprit de Clannik",
      "resume": "À quoi sert cette application, et en quoi elle diffère d’un assistant ordinaire.",
      "sections": [
        {
          "titre": "Le problème que Clannik résout",
          "paragraphes": [
            "Quand on utilise une intelligence artificielle seul, dans son coin, le travail reste dans son coin. La conversation appartient à celui qui l’a ouverte. Personne d’autre ne la voit, personne ne peut la reprendre, et si quelqu’un de la famille veut poursuivre, il recommence de zéro.",
            "Clannik est fait pour l’inverse : plusieurs personnes de la même famille travaillent sur le même sujet, avec une IA, au même moment. Ce qui est écrit est visible par tous ceux qui participent, et la réponse de l’IA s’affiche chez chacun au fur et à mesure qu’elle s’écrit.",
            "C’est la seule chose qui compte pour comprendre le reste : Clannik n’est pas un assistant personnel qu’on partagerait de temps en temps. C’est un atelier commun, où l’IA est un outil posé sur l’établi et non une conversation privée."
          ],
          "points": [
            {
              "quoi": "Et ce que Clannik ne peut pas promettre",
              "comment": "La page « Ce que Clannik voit » le dit en entier."
            }
          ]
        },
        {
          "titre": "Un cas d’usage, qui servira de fil",
          "paragraphes": [
            "Prenons une situation concrète, à laquelle cette notice reviendra chapitre après chapitre.",
            "La famille organise un grand repas de fin d’année. Mimi s’occupe du menu, sa fille Solène des courses, et les deux filles de Solène préparent ensemble un mot de bienvenue. Les quatre veulent l’aide d’une IA, mais sur le même repas — pas chacune sur sa version du repas.",
            "Dans Clannik, cela donne : un projet « Réveillon », partagé entre les quatre. Dans ce projet, trois conversations — « Le menu », « Les courses », « Le mot d’accueil ». Chacune travaille dans la sienne, les deux petites-filles à deux dans la dernière, mais toutes voient les trois et peuvent entrer dans celle des autres pour lire ou aider.",
            "Quand Mimi demande à l’IA une idée d’entrée, Solène voit la réponse arriver en direct, et peut enchaîner sans attendre que Mimi la lui raconte. C’est exactement ce qu’un assistant personnel ne permet pas."
          ]
        },
        {
          "titre": "Les quatre objets à connaître",
          "paragraphes": [
            "Tout Clannik tient sur quatre mots. Les retenir suffit à ne plus jamais être perdu."
          ],
          "points": [
            {
              "quoi": "Le projet",
              "comment": "Un classeur partagé, avec un nom et des membres. « Réveillon » est un projet. Il ne contient pas de travail par lui-même : il contient des conversations, des documents et des gens.",
              "attention": "Créer un projet ne produit rien de visible d’autre qu’un nom. C’est normal, et c’est la première surprise de tout le monde. Le travail commence à la conversation."
            },
            {
              "quoi": "La conversation",
              "comment": "Un échange avec une IA, à l’intérieur d’un projet. « Le menu » est une conversation. C’est là que vous écrivez et que l’IA répond. Tous les membres du projet la voient."
            },
            {
              "quoi": "Le document",
              "comment": "Un fichier déposé dans le projet — une liste de courses, un devis, une recette — dont l’IA peut se servir. Il appartient au projet, donc à tous ses membres."
            },
            {
              "quoi": "La consigne",
              "comment": "Une façon de demander que vous écrivez une fois et que vous reprenez ensuite. Les consignes forment une bibliothèque commune : ce que l’un écrit, toute la famille peut le lire et s’en servir."
            }
          ]
        },
        {
          "titre": "Ce que Clannik garantit",
          "paragraphes": [
            "Trois promesses tenues par la construction du logiciel, et pas seulement par intention."
          ],
          "points": [
            {
              "quoi": "Personne ne voit ce qui ne lui est pas partagé",
              "comment": "Un projet auquel vous n’avez pas été invité vous est strictement invisible : il ne s’affiche pas, et l’application répond « introuvable » plutôt que « interdit », pour ne pas même révéler son existence."
            },
            {
              "quoi": "Le travail est vraiment simultané",
              "comment": "Ce n’est pas un rafraîchissement toutes les trente secondes. Les écrans sont reliés en permanence : une réponse qui s’écrit chez l’un s’écrit chez l’autre en même temps, et l’arrivée d’un membre s’affiche aussitôt."
            },
            {
              "quoi": "Rien ne se perd quand deux personnes écrivent ensemble",
              "comment": "Si deux membres modifient la même chose au même instant, l’application refuse la seconde modification au lieu d’écraser la première, puis la rejoue automatiquement sur la version à jour."
            },
            {
              "quoi": "Et ce que Clannik ne peut pas promettre",
              "comment": "La page « Ce que Clannik voit » le dit en entier."
            }
          ]
        },
        {
          "titre": "Ce que Clannik promet, et ce qu’il ne peut pas promettre",
          "paragraphes": [
            "Clannik n’ouvre tes projets à personne que tu n’as pas invité — ni aux autres membres, ni à Charles. Aucun écran ne le permet.",
            "Mais Clannik tourne sur une machine qui appartient à Charles, et tes textes y sont rangés en clair. Qui détient la machine peut, techniquement, tout lire : Charles, et l’assistant qui l’aide à réparer l’application quand elle tombe en panne.",
            "Ce n’est donc pas un verrou qui te protège, c’est une parole. Charles s’engage à ne pas aller lire ce qui ne lui est pas partagé, et à te prévenir s’il devait le faire pour réparer quelque chose.",
            "Si cette parole ne te suffit pas, n’écris pas dans Clannik ce que tu ne confierais pas à Charles."
          ],
          "points": [
            {
              "quoi": "Ce texte est aussi lu au moment de créer son compte",
              "comment": "Il apparaît avant le choix du mot de passe, pour que personne n’écrive dans Clannik en croyant à un secret absolu."
            }
          ]
        }
      ]
    },
    {
      "id": "entrer",
      "titre": "Entrer dans l’atelier",
      "resume": "Recevoir son invitation, se connecter, se déconnecter.",
      "sections": [
        {
          "titre": "On ne s’inscrit pas : on est invité",
          "paragraphes": [
            "Clannik n’a pas de page d’inscription ouverte. Il n’y a aucun moyen de se créer un compte depuis l’extérieur, et c’est voulu : l’atelier est réservé aux adultes de la famille.",
            "Un compte naît d’une seule façon — quelqu’un qui a le droit d’inviter envoie une invitation à votre adresse électronique. Vous recevez un courrier contenant un lien. C’est en suivant ce lien que vous choisissez votre mot de passe et que votre compte existe."
          ],
          "points": [
            {
              "quoi": "Le lien d’invitation ne sert qu’une fois",
              "comment": "Dès qu’il a servi à créer votre compte, il est consommé. Le rouvrir ne fait rien.",
              "attention": "Si deux personnes cliquent sur le même lien au même instant, un seul compte est créé. L’autre reçoit un refus, jamais un second compte."
            },
            {
              "quoi": "Une invitation peut mourir sans avoir servi",
              "comment": "Elle expire au bout d’un temps, elle peut être annulée par celui qui l’a envoyée, et elle disparaît si le compte qui l’a émise est supprimé.",
              "attention": "Dans tous ces cas, le message affiché est le même. C’est délibéré : un refus qui détaillerait la cause dirait à un inconnu si une adresse existe."
            }
          ]
        },
        {
          "titre": "Se connecter",
          "paragraphes": [
            "L’écran d’entrée demande votre adresse électronique et votre mot de passe, puis vous mène directement à vos projets."
          ],
          "points": [
            {
              "quoi": "Le mot de passe fait douze caractères au minimum",
              "comment": "C’est une règle du produit, pas un réglage. L’écran vous le rappelle au moment où vous CHOISISSEZ votre mot de passe ; le serveur, lui, refuse toujours un mot de passe plus court."
            },
            {
              "quoi": "Un refus ne dit jamais pourquoi",
              "comment": "Adresse inconnue, mauvais mot de passe, compte désactivé : le message est identique dans les trois cas, et le temps de réponse aussi.",
              "attention": "C’est volontaire. Un message différent selon la cause permettrait à quelqu’un de découvrir quelles adresses ont un compte."
            },
            {
              "quoi": "On ne vous refuse jamais l’entrée",
              "comment": "Si vous connaissez votre mot de passe, vous entrez — toujours. Se tromper plusieurs fois de suite fait simplement attendre avant l’essai suivant : une fraction de seconde d’abord, puis de plus en plus, jusqu’à huit secondes au plus. Une entrée réussie remet ce compteur à zéro.",
              "attention": "Jusqu’au 8 août 2026, l’application refusait la sixième tentative pendant une minute. C’était une erreur : ce refus visait l’adresse, donc son propriétaire. Quelqu’un qui connaissait votre adresse pouvait vous fermer la porte, et en même temps votre seul chemin de retour."
            },
            {
              "quoi": "Votre session tient sans être éternelle",
              "comment": "Une fois connecté, vous le restez plusieurs jours sans avoir à retaper votre mot de passe. La session finit par expirer d’elle-même.",
              "attention": "Si votre compte est désactivé alors que vous êtes déjà connecté, votre session cesse de fonctionner immédiatement — elle ne survit pas jusqu’à son expiration."
            }
          ]
        },
        {
          "titre": "Votre profil, et sortir",
          "paragraphes": [
            "Votre profil tient en trois choses : votre adresse électronique, le nom que les autres voient, et votre rôle dans la famille. C’est tout ce que l’application affiche de vous.",
            "Se déconnecter ferme la session en cours et vous ramène à l’écran d’entrée. Les autres appareils sur lesquels vous êtes connecté ne sont pas touchés."
          ]
        },
        {
          "titre": "Si vous avez oublié votre mot de passe",
          "paragraphes": [
            "Sur l’écran d’entrée, un lien dit « J’ai oublié mon mot de passe ». Vous y donnez votre adresse électronique, et vous recevez un courrier contenant un lien pour en choisir un nouveau.",
            "Personne n’a besoin d’intervenir, et personne ne voit votre mot de passe — pas même l’administrateur de la famille, qui ne peut pas en poser un à votre place."
          ],
          "points": [
            {
              "quoi": "La réponse à l’écran est toujours la même",
              "comment": "Que l’adresse ait un compte ou non, l’écran affiche la même phrase, au conditionnel. C’est voulu : sinon, ce formulaire dirait à n’importe qui quelles adresses de la famille ont un compte.",
              "attention": "Si rien n’arrive, regardez d’abord dans les courriers indésirables, puis vérifiez l’adresse que vous avez tapée."
            },
            {
              "quoi": "Personne ne peut vous empêcher d’en demander un",
              "comment": "Cette porte ne se ferme jamais. Si beaucoup de demandes arrivent en même temps, la réponse est simplement retardée de quelques secondes — jamais refusée.",
              "attention": "C’est le seul chemin de retour : aucun administrateur, pas même celui qui a fondé la famille, ne peut poser un mot de passe à votre place."
            },
            {
              "quoi": "Le lien est valable vingt-quatre heures, et ne sert qu’une fois",
              "comment": "Passé ce délai, ou une fois utilisé, il est refusé — et le refus est le même dans tous les cas. Il suffit d’en redemander un.",
              "attention": "Demander un nouveau lien annule le précédent. Si vous en avez demandé plusieurs, seul le dernier fonctionne."
            },
            {
              "quoi": "Changer de mot de passe ferme toutes vos connexions",
              "comment": "Vos autres appareils, ou une session restée ouverte ailleurs, sont déconnectés. C’est ce qui rend le geste utile quand on craint que quelqu’un soit entré."
            },
            {
              "quoi": "Et ce que Clannik ne peut pas promettre",
              "comment": "La page « Ce que Clannik voit » le dit en entier."
            }
          ]
        }
      ]
    },
    {
      "id": "projets",
      "titre": "Les projets",
      "resume": "Créer un espace partagé, le renommer, l’archiver, le supprimer.",
      "sections": [
        {
          "titre": "Ce qu’un projet est, et ce qu’il n’est pas",
          "paragraphes": [
            "Un projet est un classeur : un nom, des membres, et ce qu’on y range — des conversations, des documents. Il ne fait rien par lui-même.",
            "C’est la source de la confusion la plus fréquente : on crée un projet, et il ne se passe rien de visible. C’est normal. Le projet est le contenant ; le travail commence quand vous ouvrez une conversation dedans.",
            "Reprenons le fil du réveillon : « Réveillon » est le projet. Il ne contient au départ que son nom et vous. Tout le reste s’y ajoute."
          ]
        },
        {
          "titre": "Créer un projet",
          "paragraphes": [
            "Sur l’écran « Vos projets », la partie « Commencer quelque chose » demande un nom et, si vous le souhaitez, une description. Le projet apparaît aussitôt dans votre liste, et vous en êtes propriétaire.",
            "Si vous êtes membre de la famille — et non parent administrateur —, l’écran vous demande aussi de choisir un RÉFÉRENT, et ce qu’il pourra faire. Voyez « Le référent d’un projet », au chapitre des droits."
          ],
          "points": [
            {
              "quoi": "La description dit de quoi il retourne",
              "comment": "Elle est facultative, et se lit sous le nom du projet — dans votre liste comme sur sa fiche. Elle sert à savoir OÙ L’ON ENTRE avant d’entrer : « préparation du Noël 2027 » en dit plus long qu’un titre.",
              "attention": "Gardez-la neutre et courte — cinq cents caractères au plus. Les détails, les avis et les décisions ont leur place dans les conversations du projet, pas dans son étiquette."
            },
            {
              "quoi": "Au départ, vous et votre référent",
              "comment": "Un projet neuf n’est partagé avec personne d’autre : les autres membres de la famille ne savent pas qu’il existe tant que vous ne les avez pas ajoutés. Le référent que vous avez choisi, lui, le voit dès sa création — et la liste des membres le nomme.",
              "attention": "Un projet créé par un parent administrateur n’a pas de référent : il est alors visible de son seul créateur au départ."
            },
            {
              "quoi": "Le nom n’a pas besoin d’être unique",
              "comment": "Deux projets peuvent porter le même nom, y compris chez vous. Ils restent distincts."
            },
            {
              "quoi": "Et ce que Clannik ne peut pas promettre",
              "comment": "La page « Ce que Clannik voit » le dit en entier."
            }
          ]
        },
        {
          "titre": "Votre liste de projets",
          "paragraphes": [
            "L’écran d’accueil montre tous les projets auxquels vous avez accès, les vôtres comme ceux qu’on vous a partagés, avec pour chacun ce que vous avez le droit d’y faire."
          ],
          "points": [
            {
              "quoi": "Chaque ligne dit votre niveau",
              "comment": "« Vous administrez ce projet », « Vous pouvez y écrire », « Vous le lisez ». C’est votre droit sur CE projet, qui peut différer de vos droits ailleurs."
            },
            {
              "quoi": "Les projets archivés restent visibles",
              "comment": "Ils apparaissent dans la liste, avec la mention « archivé »."
            }
          ]
        },
        {
          "titre": "Renommer un projet",
          "paragraphes": [
            "Le bouton « Renommer le projet », dans la partie « Le projet », change son nom pour tout le monde."
          ],
          "points": [
            {
              "quoi": "Il faut au moins le droit de modifier",
              "comment": "Un membre en lecture seule ne voit pas ce bouton. Ce n’est pas un refus quand il clique : le bouton n’existe pas pour lui."
            },
            {
              "quoi": "Deux personnes qui renomment en même temps",
              "comment": "La seconde modification est refusée plutôt que d’écraser la première, puis l’application la rejoue automatiquement sur la version à jour. Vous n’avez rien à faire, et rien n’est perdu."
            },
            {
              "quoi": "La description se modifie par le même geste",
              "comment": "Nom et description voyagent ensemble dans une seule demande, donc sous un seul arbitrage. Vider le champ efface la description.",
              "attention": "Les séparer aurait donné deux écritures concurrentes sur le même projet, chacune capable de faire échouer l’autre pour une modification que vous croyiez unique."
            }
          ]
        },
        {
          "titre": "Archiver, supprimer",
          "paragraphes": ["Deux gestes différents, qu’il ne faut pas confondre."],
          "points": [
            {
              "quoi": "Archiver ferme le projet sans rien perdre",
              "comment": "Un projet archivé se lit encore — les conversations, les documents, tout reste consultable — mais plus rien ne s’y modifie. Aucune conversation nouvelle, aucun renommage, aucun partage.",
              "attention": "C’est le geste à préférer pour un projet terminé : il se défait d’un clic, et le projet reste dans vos listes."
            },
            {
              "quoi": "Supprimer met le projet à la corbeille",
              "comment": "Le projet quitte la vue de tous ses participants — il disparaît des listes, il ne s’ouvre plus. Mais rien n’est effacé : ni les conversations, ni les messages, ni les documents déposés, ni les droits qui disent qui y travaillait.",
              "attention": "Vider la corbeille pour de bon n’existe pas encore : aucun écran ne le fait, et aucune route du serveur non plus. Le projet reste rangé sur le serveur, hors de toutes les listes."
            },
            {
              "quoi": "Seul le propriétaire peut faire l’un ou l’autre",
              "comment": "Un éditeur, même actif dans le projet, n’a ni l’archivage ni la suppression. Seule exception à la suppression : le fondateur de la famille, qui peut mettre à la corbeille n’importe quel projet — c’est sa seule façon de se défaire d’un projet dont il est le référent. Cela ne lui ouvre aucune lecture."
            }
          ]
        }
      ]
    },
    {
      "id": "droits",
      "titre": "Les membres et les droits",
      "resume": "Qui a le droit de faire quoi, et comment on ouvre un projet à quelqu’un.",
      "sections": [
        {
          "titre": "Quatre niveaux, et pas un de plus",
          "paragraphes": [
            "Votre droit se calcule projet par projet. Vous pouvez être propriétaire de l’un, simple lecteur d’un autre, et étranger à un troisième."
          ],
          "points": [
            {
              "quoi": "Propriétaire",
              "comment": "Celui qui a créé le projet. Il peut tout : lire, écrire, partager, changer le niveau des autres, les retirer, renommer, archiver, supprimer.",
              "attention": "La propriété ne se transmet pas. Elle reste à celui qui a créé le projet."
            },
            {
              "quoi": "Éditeur",
              "comment": "Il lit tout et écrit tout : ouvrir des conversations, écrire dedans, déposer des documents, renommer le projet.",
              "attention": "Il ne peut ni partager le projet, ni changer le niveau de quelqu’un, ni archiver, ni supprimer."
            },
            {
              "quoi": "Lecteur",
              "comment": "Il voit le projet, ses conversations, ses membres et ses documents. Il ne modifie rien — les boutons d’écriture ne s’affichent pas pour lui."
            },
            {
              "quoi": "Non partagé",
              "comment": "Le projet n’existe pas pour vous. Il n’apparaît nulle part, et l’application répond « introuvable » si vous tombez sur son adresse.",
              "attention": "Ce n’est pas un détail de formulation : répondre « accès refusé » révélerait que le projet existe. « Introuvable » ne révèle rien."
            }
          ]
        },
        {
          "titre": "Le rôle de famille, à ne pas confondre",
          "paragraphes": [
            "À côté du niveau sur un projet, chaque compte porte un rôle dans la famille : membre ordinaire, ou administrateur de la famille.",
            "L’administrateur invite les nouvelles personnes, annule une invitation, et peut fermer l’accès de quelqu’un — puis le rouvrir. Il ne lit pour autant aucun projet auquel il n’a pas été invité."
          ],
          "points": [
            {
              "quoi": "Être administrateur n’ouvre AUCUN projet",
              "comment": "Un administrateur de la famille n’a pas plus de droits qu’un autre sur un projet auquel il n’a pas été invité : il ne le voit pas.",
              "attention": "C’est une règle centrale de Clannik. Administrer la famille et lire le travail des autres sont deux choses séparées, et le logiciel les tient séparées."
            }
          ]
        },
        {
          "titre": "Partager un projet",
          "paragraphes": [
            "Dans la fiche du projet, la partie « Ouvrir le projet à quelqu’un » demande l’adresse électronique de la personne. Elle est ajoutée en lecture.",
            "Sur notre réveillon : Mimi crée le projet, puis y ajoute Solène et ses deux filles. Les quatre le voient aussitôt apparaître dans leur liste."
          ],
          "points": [
            {
              "quoi": "La personne doit déjà avoir un compte",
              "comment": "Partager n’envoie pas d’invitation. Si l’adresse ne correspond à aucun compte, le partage échoue."
            },
            {
              "quoi": "Seul le propriétaire partage",
              "comment": "Un éditeur ne voit pas cette partie de l’écran."
            }
          ]
        },
        {
          "titre": "Le référent d’un projet",
          "paragraphes": [
            "Quand un membre de la famille crée un projet, il doit choisir un référent : un parent administrateur — ou le fondateur — qui suivra ce projet avec lui. Sans ce choix, le projet ne se crée pas.",
            "Le référent est un participant comme un autre : il voit le projet, et le créateur choisit s’il pourra seulement le lire ou aussi y écrire. Ce n’est pas un accès caché : la liste des membres le nomme, avec le mot « référent ».",
            "Un projet créé par un parent administrateur, lui, n’a besoin d’aucun référent."
          ],
          "points": [
            {
              "quoi": "Le référent ne peut pas être retiré du projet",
              "comment": "Le propriétaire peut retirer n’importe quel autre membre, mais pas son référent : le projet se retrouverait sans personne pour le suivre."
            },
            {
              "quoi": "S’il part, le fondateur prend sa place",
              "comment": "Un référent peut quitter le projet. Il est alors remplacé aussitôt par le fondateur de la famille, qui devient référent et voit le projet en lecture. Un projet n’est donc jamais sans référent.",
              "attention": "Le fondateur, lui, ne peut pas quitter un projet dont il est le référent : il n’aurait personne à qui passer la main. Pour s’en défaire, il le met à la corbeille."
            },
            {
              "quoi": "Les projets créés avant cette règle ont reçu le fondateur pour référent",
              "comment": "Tout projet créé par un membre avant que la règle existe a le fondateur comme référent, et le fondateur y a donc accès en lecture. C’est une décision assumée, et c’est pourquoi l’écran le nomme."
            },
            {
              "quoi": "Un membre voit qui administre, sans voir la famille",
              "comment": "Pour choisir son référent, un membre obtient la liste des parents administrateurs — leurs NOMS, et rien d’autre. Ni adresse, ni état de compte, ni date. Voir la famille au complet reste réservé."
            }
          ]
        },
        {
          "titre": "Changer un niveau, retirer quelqu’un",
          "paragraphes": [
            "Dans la fiche du projet, chaque membre porte une liste déroulante « Peut lire » / « Peut lire et écrire », et un bouton « Retirer ». Le changement prend effet immédiatement, y compris pour la personne si elle a l’écran ouvert.",
            "Un projet archivé se lit, il ne se remanie plus : ces deux gestes-là disparaissent. Mais « Quitter ce projet » reste — l’archivage n’enferme personne."
          ],
          "points": [
            {
              "quoi": "Retirer un membre le déconnecte du projet sur-le-champ",
              "comment": "Il ne perd pas seulement l’accès pour la prochaine fois : s’il a la fiche du projet ouverte à cet instant, sa liaison est coupée et il cesse de recevoir ce qui s’y passe."
            },
            {
              "quoi": "Chacun peut se retirer soi-même",
              "comment": "Un membre peut quitter un projet sans demander la permission : le bouton « Quitter ce projet » est sous la liste des membres. Il ne peut pas retirer quelqu’un d’autre."
            },
            {
              "quoi": "Le propriétaire ne peut pas se retirer",
              "comment": "Un projet sans propriétaire n’aurait plus personne pour l’administrer : le bouton « Quitter ce projet » ne s’affiche pas pour lui. Pour s’en défaire, il faut l’archiver ou le mettre à la corbeille."
            }
          ]
        }
      ]
    },
    {
      "id": "famille",
      "titre": "Administrer la famille",
      "resume": "Inviter quelqu’un, fermer un accès, le rouvrir.",
      "sections": [
        {
          "titre": "L’écran de la famille",
          "paragraphes": [
            "Le parent administrateur dispose d’une entrée « La famille » en haut de l’écran, à côté de « Comment ça marche ». Elle n’apparaît que pour lui.",
            "On y voit deux choses, séparées : les personnes qui ont un compte, et les invitations envoyées."
          ],
          "points": [
            {
              "quoi": "Un autre membre n’y accède pas, même en tapant l’adresse",
              "comment": "L’écran ne lui montre rien de la famille : le serveur refuse, et le refus s’affiche. Cacher l’entrée n’est pas ce qui protège — c’est le serveur."
            }
          ]
        },
        {
          "titre": "Inviter quelqu’un",
          "paragraphes": [
            "Vous donnez l’adresse électronique de la personne, et ce qu’elle pourra faire : membre, ou parent administrateur. Elle reçoit un courrier avec un lien ; en le suivant, elle choisit son nom et son mot de passe, et se retrouve dans l’atelier."
          ],
          "points": [
            {
              "quoi": "Donnez une adresse qui reçoit vraiment le courrier",
              "comment": "L’invitation part vers l’adresse que vous indiquez, et c’est aussi celle qui servira si la personne oublie son mot de passe. Une adresse qui ne reçoit rien rend ces deux chemins inutilisables.",
              "attention": "Invitez chacun sur l’adresse dont il se sert tous les jours. Les adresses en « chalou.link » sont celles du service lui-même, pas des adresses de personnes : c’est de là que Clannik écrit, et vous pouvez lui répondre à contact@clannik.chalou.link."
            },
            {
              "quoi": "Si le courrier ne part pas, le lien s’affiche",
              "comment": "L’invitation existe quand même. L’écran vous montre alors le lien à transmettre vous-même, par le moyen que vous voulez.",
              "attention": "Ce lien vaut création de compte : ne le transmettez qu’à la personne visée."
            },
            {
              "quoi": "Une invitation en attente peut être annulée",
              "comment": "Le bouton « Annuler » la rend inutilisable sur-le-champ. Une invitation non utilisée expire d’elle-même au bout de sept jours."
            }
          ]
        },
        {
          "titre": "Fermer un accès, et le rouvrir",
          "paragraphes": [
            "Fermer l’accès de quelqu’un coupe tout : il ne peut plus entrer, ses connexions en cours sont refusées, et les conversations qu’il avait ouvertes se ferment en quelques secondes.",
            "Rien n’est effacé. Ses projets, ses messages et ses documents restent en place, et rouvrir son accès les lui rend tels quels."
          ],
          "points": [
            {
              "quoi": "Fermer demande deux clics, rouvrir un seul",
              "comment": "Le premier clic transforme le bouton en une phrase qui nomme la personne ; le second fait le geste. C’est le geste dangereux qui se confirme, pas l’autre."
            },
            {
              "quoi": "Le dernier parent administrateur ne peut pas être fermé",
              "comment": "Sinon plus personne ne pourrait administrer la famille — ni rouvrir les accès fermés. Invitez d’abord un autre parent administrateur."
            },
            {
              "quoi": "Effacer le compte de quelqu’un d’autre n’existe pas",
              "comment": "Seule la personne elle-même peut effacer son compte, avec son mot de passe. Fermer un accès suffit à tout couper, sans poser la question de ce que deviendraient ses projets.",
              "attention": "La propriété d’un projet ne se transmet pas : effacer quelqu’un emporterait des projets où d’autres travaillent peut-être."
            }
          ]
        },
        {
          "titre": "Le compte qui a fondé la famille",
          "paragraphes": [
            "Un compte porte une marque à part : celui qui a fondé la famille. Son accès ne se ferme pas depuis l’écran, et il ne peut pas s’effacer lui-même. Aucun bouton ne le propose."
          ],
          "points": [
            {
              "quoi": "Pourquoi cette exception",
              "comment": "C’est le garde-fou contre l’enfermement définitif : un clic de trop, un soir de fatigue, et plus personne n’administrerait rien.",
              "attention": "Ce compte peut être ROUVERT s’il a été fermé autrement. La règle ne protège que dans un sens : elle empêche de fermer, jamais d’ouvrir."
            }
          ]
        }
      ]
    },
    {
      "id": "conversations",
      "titre": "Les conversations",
      "resume": "Là où le travail se fait vraiment : poser une question, recevoir une réponse.",
      "sections": [
        {
          "titre": "Une conversation par sujet",
          "paragraphes": [
            "Une conversation est un échange suivi avec une IA, rangé dans un projet. C’est l’équivalent d’un fil de discussion : vos questions et les réponses, dans l’ordre.",
            "Sur notre réveillon, le projet contient trois conversations — « Le menu », « Les courses », « Le mot d’accueil ». Chacune garde son sujet, et son historique reste lisible sans être mêlé aux autres.",
            "Découper par sujet n’est pas de la coquetterie : l’IA lit ce qui précède dans la conversation. Mélanger le menu et le mot d’accueil dans un seul fil donne des réponses plus confuses."
          ]
        },
        {
          "titre": "Créer une conversation",
          "paragraphes": [
            "Dans la fiche du projet, la partie « Le travail en cours » demande un titre, et le bouton « Créer la conversation » vous emmène directement dedans."
          ],
          "points": [
            {
              "quoi": "Le titre sert à la retrouver",
              "comment": "Il apparaît dans la liste des conversations du projet, pour vous et pour tous les autres membres."
            },
            {
              "quoi": "Un lecteur ne peut pas en créer",
              "comment": "La partie « Le travail en cours » lui montre la liste, mais pas le champ de création."
            },
            {
              "quoi": "Un projet archivé n’en accepte plus",
              "comment": "Les conversations existantes restent lisibles ; aucune nouvelle ne s’ouvre."
            }
          ]
        },
        {
          "titre": "Écrire, et recevoir la réponse",
          "paragraphes": [
            "Vous écrivez votre demande, vous validez, et la réponse de l’IA s’écrit sous vos yeux, par morceaux, à mesure qu’elle arrive. Vous n’attendez pas devant un écran vide qu’un bloc entier apparaisse."
          ],
          "points": [
            {
              "quoi": "Les autres membres voient la même chose, en même temps",
              "comment": "Si Solène a la conversation ouverte pendant que Mimi pose sa question, la réponse se forme chez Solène exactement comme chez Mimi."
            },
            {
              "quoi": "Une génération à la fois par conversation",
              "comment": "Tant qu’une réponse est en cours d’écriture dans une conversation, une seconde demande n’y démarre pas. Les autres conversations du projet ne sont pas bloquées pour autant."
            },
            {
              "quoi": "Ce qui est écrit dans une conversation y reste",
              "comment": "Les messages appartiennent à la conversation, qui appartient au projet. Rien ne circule d’un projet à l’autre."
            }
          ]
        },
        {
          "titre": "Arrêter une réponse en cours",
          "paragraphes": [
            "Une réponse partie de travers n’a pas besoin d’aller à son terme : elle s’annule pendant qu’elle s’écrit."
          ],
          "points": [
            {
              "quoi": "Seul celui qui a lancé la demande peut l’annuler",
              "comment": "Un autre membre qui regarde la réponse arriver ne peut pas l’interrompre. C’est celui qui a posé la question qui décide."
            },
            {
              "quoi": "Ce qui est déjà écrit est conservé",
              "comment": "L’annulation arrête la suite ; elle n’efface pas le début de la réponse, qui reste dans le fil."
            }
          ]
        }
      ]
    },
    {
      "id": "ensemble",
      "titre": "Travailler à plusieurs en même temps",
      "resume": "Qui est là, ce que chacun voit arriver, et ce qui se passe en cas de collision.",
      "sections": [
        {
          "titre": "Voir qui est là",
          "paragraphes": [
            "Quand vous ouvrez la fiche d’un projet, la partie « Qui travaille ici » liste ses membres, et signale d’une pastille ceux qui sont présents à cet instant.",
            "La phrase en haut du projet dit la même chose autrement : « Vous êtes seul ici pour l’instant », ou le nombre de personnes en train d’y travailler."
          ],
          "points": [
            {
              "quoi": "La présence se met à jour toute seule",
              "comment": "Quand Solène ouvre le projet, sa pastille apparaît chez Mimi sans que celle-ci ait à recharger quoi que ce soit. Quand elle ferme, la pastille disparaît."
            },
            {
              "quoi": "Être présent, c’est avoir l’écran du projet ouvert",
              "comment": "Ce n’est pas « être connecté à Clannik ». Quelqu’un connecté mais occupé sur un autre projet n’est pas présent ici."
            }
          ]
        },
        {
          "titre": "Ce qui arrive en direct",
          "paragraphes": [
            "Votre écran est relié en permanence au projet ouvert. Il ne va pas chercher les nouvelles de temps en temps : elles lui parviennent."
          ],
          "points": [
            {
              "quoi": "Les réponses de l’IA, à mesure qu’elles s’écrivent",
              "comment": "Chez tous ceux qui ont la conversation ouverte, pas seulement chez l’auteur."
            },
            {
              "quoi": "Les arrivées et les départs",
              "comment": "Les pastilles de présence changent sans intervention."
            },
            {
              "quoi": "La vie des documents",
              "comment": "Un document déposé, en cours de lecture, prêt, refusé ou supprimé : chaque étape s’affiche chez les présents dans l’ordre où elle se produit."
            },
            {
              "quoi": "Rien n’arrive à qui n’y a pas droit",
              "comment": "Quelqu’un qui n’est pas membre du projet ne reçoit aucun de ces messages. Le lien qu’il tenterait d’ouvrir est refusé de la même façon que l’écran lui-même."
            }
          ]
        },
        {
          "titre": "Quand le lien se coupe",
          "paragraphes": [
            "Si la liaison avec l’atelier tombe — réseau perdu, session expirée, compte désactivé — l’écran vous le dit franchement, en haut du projet, et vous demande de recharger la page."
          ],
          "points": [
            {
              "quoi": "Une page silencieuse serait pire",
              "comment": "Sans ce message, l’écran continuerait d’afficher un état figé qui a l’air vivant, et vous croiriez que personne ne travaille alors que vous ne recevez plus rien."
            }
          ]
        },
        {
          "titre": "Deux personnes qui modifient la même chose",
          "paragraphes": [
            "C’est le cas que le travail simultané rend inévitable, et Clannik le traite sans jamais perdre de travail."
          ],
          "points": [
            {
              "quoi": "La seconde modification est refusée, pas écrasée",
              "comment": "Si Mimi et Solène renomment le projet au même instant, la seconde demande arrive sur une version qui n’est plus la bonne. Elle est rejetée."
            },
            {
              "quoi": "Puis rejouée automatiquement",
              "comment": "L’application recharge la version à jour et refait la modification dessus, une fois. Vous ne voyez rien de cette mécanique, et rien n’est perdu.",
              "attention": "Sans ce rejeu, vous auriez à recliquer sur un écran périmé, et l’échec se répéterait à l’identique."
            }
          ]
        }
      ]
    },
    {
      "id": "reprendre",
      "titre": "Reprendre une réponse",
      "resume": "Corriger sa question, relancer autrement, et retrouver les versions précédentes.",
      "sections": [
        {
          "titre": "Corriger ce qu’on a écrit",
          "paragraphes": [
            "Une question mal posée se corrige sur place, sans réécrire tout le fil."
          ],
          "points": [
            {
              "quoi": "Vous ne modifiez que vos propres messages",
              "comment": "La parole d’un autre membre ne se réécrit pas. Mimi ne peut pas corriger ce que Solène a écrit, même si elle est propriétaire du projet."
            },
            {
              "quoi": "Deux corrections simultanées ne s’écrasent pas",
              "comment": "Comme partout dans Clannik, la seconde est refusée plutôt que d’effacer la première."
            }
          ]
        },
        {
          "titre": "Les branches : essayer sans perdre",
          "paragraphes": [
            "C’est la notion la moins évidente de Clannik, et la plus utile. Une conversation n’est pas forcément une ligne droite : elle peut se diviser.",
            "Reprenons le menu du réveillon. L’IA propose un menu, il ne convient pas. Vous pouvez repartir de votre question et demander autre chose — sans effacer la première réponse. Les deux existent alors côte à côte, dans deux branches de la même conversation.",
            "Une branche est donc une version du fil à partir d’un point donné. La conversation en a toujours au moins une, celle d’origine."
          ],
          "points": [
            {
              "quoi": "Régénérer crée une branche, ça n’écrase rien",
              "comment": "Relancer une réponse depuis un message ouvre une nouvelle branche à cet endroit. L’ancienne reste entière, consultable quand vous voulez."
            },
            {
              "quoi": "Un sélecteur permet de passer d’une branche à l’autre",
              "comment": "Vous choisissez celle que vous regardez ; le fil affiché change en conséquence."
            },
            {
              "quoi": "L’écran ne montre que la branche ouverte",
              "comment": "Une réponse qui s’écrit dans une autre branche, ou dans une autre conversation du même projet, ne vient pas se mêler à ce que vous lisez.",
              "attention": "Ce point a été un vrai défaut, corrigé : l’écran affichait la réponse d’un autre fil dans le fil ouvert. Il porte maintenant sa conversation et sa branche sur chaque message."
            }
          ]
        },
        {
          "titre": "Ce qui reste, ce qui part",
          "paragraphes": [
            "Rien de ce que vous avez écrit ou reçu ne disparaît d’une conversation par un de ces gestes. Corriger un message garde le fil, régénérer ajoute une branche, annuler conserve le début reçu.",
            "La seule chose qui efface vraiment est la suppression du projet, décrite au chapitre des projets."
          ]
        }
      ]
    },
    {
      "id": "modeles",
      "titre": "Choisir le modèle d’IA",
      "resume": "Qui répond à vos questions, comment en changer, et ce que cela coûte.",
      "sections": [
        {
          "titre": "Le choix se fait juste au-dessus de votre message",
          "paragraphes": [
            "Dans une conversation, au-dessus du champ où vous écrivez, une liste déroulante nommée « Modèle » indique qui va répondre. Vous en changez quand vous voulez : le choix vaut pour la question que vous envoyez, pas pour toute la conversation.",
            "Sur notre réveillon : Mimi peut demander le menu à un modèle, puis poser la question suivante à un autre pour voir s’il répond mieux. Les deux réponses restent dans le même fil, l’une sous l’autre."
          ],
          "points": [
            {
              "quoi": "Changer de modèle",
              "comment": "Ouvrez la liste, choisissez, écrivez votre question, envoyez. La réponse viendra du modèle affiché au moment de l’envoi.",
              "attention": "Changer de modèle ne réécrit rien : les réponses déjà reçues restent telles que le modèle précédent les a données."
            },
            {
              "quoi": "Ce que la liste contient",
              "comment": "Uniquement les modèles inscrits au catalogue de votre atelier — jamais tous ceux qui existent chez un fournisseur.",
              "attention": "C’est volontaire, et c’est la protection principale : un intermédiaire comme OpenRouter donne accès à des centaines de modèles de toutes origines. Le catalogue est ce qui referme cette porte, modèle par modèle."
            }
          ]
        },
        {
          "titre": "Deux façons de payer, selon le modèle",
          "paragraphes": [
            "Certains modèles passent par un abonnement déjà réglé : les utiliser ne coûte pas un centime de plus. D’autres sont facturés à l’usage, au volume de texte envoyé et reçu."
          ],
          "points": [
            {
              "quoi": "Un modèle d’abonnement",
              "comment": "Il ne consomme aucun euro. Il compte tout de même contre le plafond, en nombre d’appels — voir le chapitre sur le budget.",
              "attention": "« Ne coûte rien de plus » ne veut pas dire « sans limite » : l’abonnement a son propre quota chez le fournisseur, que Clannik ne connaît pas."
            },
            {
              "quoi": "Un modèle facturé à l’usage",
              "comment": "Avant même que la question parte, Clannik met de côté la somme maximale qu’elle pourrait coûter. Quand la réponse est arrivée, cette somme est ramenée au coût réel.",
              "attention": "Le coût réel est presque toujours inférieur à ce qui a été mis de côté : la mise de côté est une sécurité, pas une facture."
            }
          ]
        },
        {
          "titre": "Ajouter ou retirer un modèle",
          "paragraphes": [
            "Le catalogue est la seule source des modèles disponibles et de leurs prix. Rien n’est écrit en dur ailleurs dans le logiciel."
          ],
          "points": [
            {
              "quoi": "Qui peut le changer",
              "comment": "Le parent administrateur seul. Tous les autres membres le lisent, et le voient dans leur liste déroulante."
            },
            {
              "quoi": "Retirer un modèle",
              "comment": "On le désactive plutôt que de l’effacer : il disparaît de la liste, et les réponses qu’il a déjà données restent lisibles avec son nom."
            },
            {
              "quoi": "Comment on administre le catalogue",
              "comment": "Par l’écran « L’argent », dans la barre du haut. Le parent administrateur y inscrit un modèle, corrige son nom ou ses prix, et l’éteint. Éteindre le retire du choix de tout le monde — y compris du sien.",
              "attention": "Un modèle ne s’efface jamais : on l’éteint. Les réponses qu’il a déjà données restent lisibles avec son nom."
            }
          ]
        },
        {
          "titre": "Quand la réponse ne part pas",
          "paragraphes": [
            "Deux refus concernent le modèle lui-même. Dans les deux cas, rien n’est envoyé et rien n’est compté."
          ],
          "points": [
            {
              "quoi": "« Ce fournisseur n’est pas configuré sur ce serveur. »",
              "comment": "Le modèle est bien au catalogue, mais la clé qui permet de l’appeler manque sur le serveur. Choisissez un autre modèle en attendant."
            },
            {
              "quoi": "« Une génération est déjà en cours sur cette branche. »",
              "comment": "Une conversation ne fabrique qu’une réponse à la fois. Attendez la fin, ou annulez la génération en cours."
            }
          ]
        }
      ]
    },
    {
      "id": "comparer",
      "titre": "Comparer plusieurs modèles",
      "resume": "Poser la même question à deux ou trois modèles, en sachant d’abord ce que cela coûtera.",
      "sections": [
        {
          "titre": "À quoi cela sert",
          "paragraphes": [
            "Quand une question est importante et qu’on ne sait pas à qui la poser, on peut l’envoyer à plusieurs modèles d’un coup et lire les réponses les unes après les autres, dans le même fil.",
            "Sur notre réveillon : Mimi demande une idée d’entrée pour douze personnes à trois modèles, et garde la formulation qui lui parle le plus."
          ]
        },
        {
          "titre": "Le déroulé, étape par étape",
          "paragraphes": [
            "Le bouton « Comparer des modèles » se trouve à côté d’« Envoyer ». Il ne s’allume que si vous avez écrit quelque chose et que le catalogue contient au moins deux modèles."
          ],
          "points": [
            {
              "quoi": "Choisir les modèles",
              "comment": "Cochez-en deux ou trois dans la liste qui s’ouvre.",
              "attention": "Au-delà de trois, les cases restantes se désactivent d’elles-mêmes. L’erreur est empêchée plutôt que signalée après coup."
            },
            {
              "quoi": "Estimer le coût",
              "comment": "Le bouton « Estimer le coût » est une étape obligée : tant que vous n’avez pas estimé, le bouton de confirmation n’existe pas à l’écran.",
              "attention": "C’est délibéré. Lancer plusieurs appels sans savoir ce qu’ils coûtent est exactement ce que ce garde-fou empêche."
            },
            {
              "quoi": "Lire l’estimation",
              "comment": "Elle annonce combien d’appels partiront — un par modèle — puis, pour chacun : « au plus » tel montant, ou, pour un modèle d’abonnement, qu’il compte contre son quota et non en euros.",
              "attention": "C’est un maximum, jamais le prix final. Le coût réel se calcule sur ce qui a vraiment été échangé."
            },
            {
              "quoi": "Confirmer, ou renoncer",
              "comment": "« Confirmer et comparer » lance les appels. « Renoncer » ferme la boîte sans rien envoyer.",
              "attention": "Comparer trois modèles coûte trois fois : c’est le prix d’un choix éclairé, et l’estimation vous l’a dit avant."
            }
          ]
        },
        {
          "titre": "Ce que vous obtenez",
          "paragraphes": [
            "Chaque modèle répond dans sa propre branche de la conversation, avec son nom. Vous passez de l’une à l’autre par le sélecteur de branche, et vous gardez celle que vous voulez : les autres ne disparaissent pas."
          ]
        }
      ]
    },
    {
      "id": "budget",
      "titre": "Le budget et les plafonds",
      "resume": "Combien la famille peut dépenser, qui paie, et ce qui arrive quand la limite est atteinte.",
      "sections": [
        {
          "titre": "Deux plafonds, jamais un seul",
          "paragraphes": [
            "Il existe un plafond pour toute la famille, et un plafond pour chaque personne. Une question doit passer les deux : le vôtre, et celui de la maison."
          ],
          "points": [
            {
              "quoi": "Voir où vous en êtes",
              "comment": "Cliquez « L’argent » dans la barre du haut. L’écran dit ce qu’il vous reste réellement — les deux plafonds croisés — et non le vôtre seul.",
              "attention": "Si l’un des deux plafonds est fermé, l’écran le dit franchement au lieu d’annoncer un reste qui ne partirait pas."
            },
            {
              "quoi": "Qui règle les plafonds",
              "comment": "Le parent administrateur seul. Poser un plafond sur quelqu’un qui n’a pas de compte est refusé."
            },
            {
              "quoi": "Comment on les règle",
              "comment": "Par l’écran « L’argent », dans la barre du haut. Le parent administrateur y pose le plafond de la famille et celui de chaque personne, en euros. Les autres y lisent le leur.",
              "attention": "Avant d’enregistrer un plafond à zéro, l’écran vous avertit que zéro ferme tout — y compris les modèles compris dans un abonnement."
            },
            {
              "quoi": "Un plafond à zéro",
              "comment": "Aucun plafond, ou un plafond à zéro, refuse TOUT — y compris les modèles d’abonnement, qui ne coûtent pourtant rien.",
              "attention": "C’est voulu : sans cette règle, un modèle « gratuit » contournerait le garde-fou familial. Par défaut, la porte est fermée."
            }
          ]
        },
        {
          "titre": "Comment une dépense est comptée",
          "paragraphes": [
            "Avant qu’une question parte, Clannik met de côté la somme maximale qu’elle pourrait coûter. Quand la réponse est complète, cette somme est remplacée par le coût réel. Vous ne payez jamais l’estimation."
          ],
          "points": [
            {
              "quoi": "Un seul payeur par question",
              "comment": "Celui qui pose la question. Les autres membres du projet voient la réponse s’écrire chez eux sans que cela touche leur plafond."
            },
            {
              "quoi": "Si l’atelier s’arrête au milieu",
              "comment": "La somme mise de côté est rendue toute seule au bout d’une dizaine de minutes. Rien ne reste bloqué pour de bon."
            },
            {
              "quoi": "Les modèles d’abonnement",
              "comment": "Ils ne consomment pas d’euros, mais chaque appel est enregistré. Le plafond doit tout de même être ouvert pour qu’ils partent."
            }
          ]
        },
        {
          "titre": "Quand le plafond est atteint",
          "paragraphes": [
            "La question est refusée avant d’être envoyée. Rien n’est dépensé, et le message vous le dit sans détour."
          ],
          "points": [
            {
              "quoi": "« Le plafond familial est atteint : la génération est refusée. »",
              "comment": "La maison a consommé son enveloppe. Il faut que le parent administrateur la relève."
            },
            {
              "quoi": "« Votre plafond est atteint : la génération est refusée. »",
              "comment": "La famille a encore de la marge, mais vous avez atteint la vôtre."
            },
            {
              "quoi": "Votre message, lui, reste écrit",
              "comment": "Ce que vous aviez tapé demeure dans la conversation, même si aucune réponse n’est partie. C’est voulu : votre texte ne se perd pas.",
              "attention": "Il sera relu comme contexte à la prochaine question du fil. Si ce n’est pas ce que vous voulez, modifiez-le avant de relancer."
            }
          ]
        }
      ]
    },
    {
      "id": "documents",
      "titre": "Les documents",
      "resume": "Déposer un fichier dans un projet, suivre sa lecture, le supprimer.",
      "sections": [
        {
          "titre": "À quoi ça sert",
          "paragraphes": [
            "Un document déposé dans un projet est lu par Clannik, qui en extrait le texte. Ce texte devient disponible pour le travail du projet.",
            "Sur notre réveillon : Solène dépose la liste des courses de l’an dernier. Le texte en est tiré, et la famille peut s’appuyer dessus plutôt que de tout retaper.",
            "Un document appartient au projet, pas à celui qui l’a déposé. Tous les membres y ont accès selon leur niveau."
          ]
        },
        {
          "titre": "Les quatre formats acceptés",
          "paragraphes": ["Clannik lit quatre formats, et refuse tout le reste."],
          "points": [
            {
              "quoi": "PDF, TXT, Markdown et DOCX",
              "comment": "Ce sont les seuls. Un fichier d’un autre type est refusé au dépôt.",
              "attention": "Le refus se fonde sur le format déclaré, sans indulgence : ni les variantes de casse, ni les espaces, ni un nom trompeur ne passent."
            },
            {
              "quoi": "Un fichier renommé ne trompe personne",
              "comment": "Une image renommée en « .txt » est acceptée au dépôt puis refusée à la lecture : son contenu n’est pas du texte."
            }
          ]
        },
        {
          "titre": "Les limites, en clair",
          "paragraphes": ["Trois limites, toutes trois refusées proprement plutôt que subies."],
          "points": [
            {
              "quoi": "Dix mégaoctets par fichier",
              "comment": "Au-delà, le dépôt est refusé, et le fichier n’est même pas lu."
            },
            {
              "quoi": "Cinquante pages par document",
              "comment": "Compté après ouverture, avant d’en tirer le texte — le comptage coûte peu, l’extraction coûte cher."
            },
            {
              "quoi": "Cinq cents mégaoctets par projet, tous documents confondus",
              "comment": "Quand le projet approche de son plafond, seuls les dépôts qui tiennent encore sont acceptés. Supprimer un document rend la place, exactement une fois.",
              "attention": "Ce compte ne dérive pas, même si plusieurs personnes déposent et suppriment au même instant : il ne peut ni dépasser le plafond ni devenir négatif."
            },
            {
              "quoi": "Un fichier qui se déplie démesurément est arrêté",
              "comment": "Certains fichiers, petits sur le disque, occupent une mémoire énorme une fois ouverts. Clannik surveille ce que la lecture consomme réellement et l’interrompt si elle enfle, avec un refus nommé."
            }
          ]
        },
        {
          "titre": "La vie d’un document, vue par tous",
          "paragraphes": [
            "Un document passe par des états, et chacun s’affiche en direct chez les membres présents dans le projet."
          ],
          "points": [
            {
              "quoi": "Importé",
              "comment": "Le fichier est arrivé, il attend d’être lu."
            },
            {
              "quoi": "En traitement",
              "comment": "Clannik en extrait le texte. Un document à la fois, jamais deux ensemble."
            },
            {
              "quoi": "Prêt",
              "comment": "Le texte est disponible."
            },
            {
              "quoi": "Refusé",
              "comment": "La lecture n’a pas abouti — format illisible, trop long, trop lourd, trop lent. La raison est nommée, pas vague."
            },
            {
              "quoi": "Supprimé",
              "comment": "Le document a été retiré du projet."
            },
            {
              "quoi": "Rien ne reste bloqué « en traitement »",
              "comment": "Si la lecture est interrompue brutalement, Clannik la conclut de lui-même en refus plutôt que de laisser un document en attente indéfinie."
            }
          ]
        },
        {
          "titre": "Supprimer un document",
          "paragraphes": [
            "La suppression est réelle : le fichier quitte le stockage, sa fiche quitte la base, et la place qu’il occupait est rendue au projet."
          ],
          "points": [
            {
              "quoi": "Supprimer deux fois ne rend pas la place deux fois",
              "comment": "Si deux personnes suppriment le même document au même instant, une seule suppression aboutit. Le compte du projet reste juste."
            },
            {
              "quoi": "Après suppression, le texte n’est plus consultable",
              "comment": "La demande de son contenu répond « introuvable »."
            }
          ]
        }
      ]
    },
    {
      "id": "prompts",
      "titre": "Les consignes",
      "resume": "Enregistrer une consigne utile, la décrire, la retrouver, la copier — et savoir que toute la famille la lit.",
      "sections": [
        {
          "titre": "Une consigne qu’on écrit une fois",
          "paragraphes": [
            "Une consigne est une façon de demander que vous avez trouvée bonne. Vous l’écrivez une fois, vous lui donnez un titre, et vous la reprenez ensuite sans la retaper.",
            "Sur notre réveillon : Mimi a trouvé la bonne façon de demander un menu — préciser le nombre de convives, les allergies, le budget, et exiger une liste de courses en fin de réponse. Plutôt que de réécrire ce paragraphe chaque année, elle l’enregistre.",
            "Les consignes ont leur écran : « Consignes », dans la barre du haut, depuis n’importe quel écran une fois connecté."
          ],
          "points": [
            {
              "quoi": "Une consigne, et sa description",
              "comment": "Le titre dit ce qu’elle fait ; la description, facultative, dit à quoi elle sert ou quand s’en servir. C’est aussi par un mot de cette description qu’on la retrouve.",
              "attention": "Le texte de la consigne peut aller jusqu’à vingt mille caractères."
            }
          ]
        },
        {
          "titre": "Une bibliothèque commune à la famille",
          "paragraphes": [
            "Toutes les consignes enregistrées sont lisibles par tous les membres de la famille. Il n’y a pas de consigne privée."
          ],
          "points": [
            {
              "quoi": "Ce que chacun voit",
              "comment": "Toute la famille voit toutes les consignes, les retrouve par la recherche et les réutilise. Seul l’auteur d’une consigne peut la modifier ou l’effacer.",
              "attention": "Un parent administrateur ne voit ni plus ni moins que les autres : c’est la règle commune, pas un pouvoir d’administrateur."
            },
            {
              "quoi": "Pourquoi il n’y a pas de consigne privée",
              "comment": "Réutiliser une consigne insère son texte dans la conversation, où tous les participants du projet le lisent. Une consigne « privée » n’aurait donc protégé que le rangement, jamais l’usage : elle aurait promis un secret qu’elle ne tenait pas.",
              "attention": "Écrivez donc vos consignes comme vous écririez un message à la famille."
            }
          ]
        },
        {
          "titre": "Retrouver une consigne",
          "paragraphes": [
            "La recherche cherche dans les titres, dans les descriptions et dans le texte des consignes. Un mot dont vous vous souvenez suffit, même s’il n’est ni dans le titre ni dans le texte, mais seulement dans la description."
          ],
          "points": [
            {
              "quoi": "La recherche cherche dans toute la bibliothèque",
              "comment": "Elle trouve les consignes de tout le monde, par un mot de leur titre, de leur description ou de leur texte.",
              "attention": "C’est la même règle qu’à la liste : rien n’est caché à la recherche qui serait visible ailleurs, ni l’inverse."
            }
          ]
        },
        {
          "titre": "Copier, modifier, supprimer",
          "paragraphes": [
            "Chaque consigne porte un bouton « Copier le texte ». Le texte part dans le presse-papier, et vous le collez où vous voulez — dans une conversation de Clannik, ou ailleurs. L’écran vous dit quand c’est fait.",
            "Clannik ne sait PAS encore insérer une consigne directement dans une conversation à votre place. Ce sera peut-être fait un jour ; aujourd’hui, c’est vous qui collez."
          ],
          "points": [
            {
              "quoi": "Le texte est copié sans la moindre altération",
              "comment": "Accents, guillemets français, tirets longs, symboles, retours à la ligne et tabulations : ce que vous avez enregistré est ce qui est copié, caractère pour caractère.",
              "attention": "Si votre navigateur refuse la copie, l’écran le dit au lieu de faire semblant : ouvrez le texte, il se sélectionne à la main."
            },
            {
              "quoi": "On ne réécrit pas la consigne d’un autre",
              "comment": "Une consigne est lisible et copiable par tous, mais seul son auteur la modifie ou la supprime. Ce n’est pas un bouton caché : le serveur refuse."
            },
            {
              "quoi": "Deux modifications en même temps ne s’écrasent pas",
              "comment": "La règle de Clannik vaut ici aussi : la seconde est refusée, pas absorbée."
            }
          ]
        }
      ]
    },
    {
      "id": "compte",
      "titre": "Votre compte et vos données",
      "resume": "Emporter ce qui vous appartient, ou partir définitivement.",
      "sections": [
        {
          "titre": "Où trouver votre compte, et comment sortir",
          "paragraphes": [
            "Une fois connecté, la barre du haut porte votre nom et votre rôle — parent administrateur, ou membre.",
            "Deux entrées y répondent à ce qui vous concerne : « Mon compte », qui ouvre votre fiche, et « Se déconnecter », qui ferme votre session."
          ],
          "points": [
            {
              "quoi": "Se déconnecter ne demande aucune confirmation",
              "comment": "Ce n’est pas un geste destructif : vous vous reconnectez avec votre adresse et votre mot de passe."
            },
            {
              "quoi": "Emporter vos données et supprimer votre compte sont sur « Mon compte »",
              "comment": "Les deux gestes se trouvent sur cette même fiche, chacun dans sa partie : « Emporter une copie de mes données », puis « Supprimer mon compte »."
            }
          ]
        },
        {
          "titre": "Emporter vos données",
          "paragraphes": [
            "Vous pouvez demander à tout moment un export de ce que Clannik détient sur vous. Il vous est rendu directement, sans démarche ni délai.",
            "Sur « Mon compte », la partie « Emporter une copie de mes données » porte un lien : il enregistre un fichier dans les téléchargements de votre navigateur."
          ],
          "points": [
            {
              "quoi": "Le fichier ne contient pas encore tout",
              "comment": "Il porte votre adresse, votre nom affiché, votre rôle, la date de création de votre compte et les dates de vos connexions. Il ne porte NI vos projets, NI vos conversations, NI les documents déposés, NI vos consignes. L’écran l’écrit lui aussi, juste au-dessus du lien.",
              "attention": "Ne comptez pas sur cet export pour emporter votre travail : il n’y est pas."
            },
            {
              "quoi": "Si rien n’arrive, regardez dans les téléchargements",
              "comment": "L’écran annonce que le fichier a été DEMANDÉ, jamais qu’il est arrivé : la page ne peut pas le savoir. Ouvrez la liste des téléchargements de votre navigateur. Si le fichier n’y est pas, rien n’a été enregistré — recliquez le lien."
            },
            {
              "quoi": "L’export ne contient aucun secret technique",
              "comment": "Ni votre mot de passe, ni l’empreinte qui en tient lieu, ni les jetons de vos sessions n’y figurent — ces choses ne sont pas des données vous concernant, ce sont des serrures."
            },
            {
              "quoi": "Le fichier se pose sur cet ordinateur",
              "comment": "S’il est partagé avec d’autres personnes, elles pourront le trouver dans les téléchargements. Rangez-le ou effacez-le une fois lu."
            }
          ]
        },
        {
          "titre": "Supprimer votre compte",
          "paragraphes": [
            "La suppression est définitive et demande de prouver que c’est bien vous : votre mot de passe est exigé au moment du geste.",
            "Le bouton « Supprimer mon compte » ouvre une fenêtre qui redit ce qui va arriver. Rien n’est effacé tant que vous n’avez pas cliqué « Oui, supprimer mon compte » DANS cette fenêtre. Le curseur arrive sur « Annuler », et la touche Échap ferme la fenêtre sans rien faire.",
            "Ce qui part avec vous est compté et vous est annoncé — le nombre d’éléments effacés figure dans la réponse."
          ],
          "points": [
            {
              "quoi": "Vos invitations en attente sont révoquées",
              "comment": "Une invitation que vous aviez envoyée et qui n’a pas encore servi cesse de fonctionner."
            },
            {
              "quoi": "Le compte qui a fondé la famille ne s’efface pas depuis l’écran",
              "comment": "Il tient toute la famille : un clic de trop ne doit pas pouvoir l’emporter. L’écran refuse et le dit. Son retrait se demande, et il se fait sur le serveur."
            },
            {
              "quoi": "Le dernier administrateur ne peut pas partir",
              "comment": "Si vous êtes le seul administrateur de la famille, la suppression est refusée : plus personne ne pourrait inviter ni administrer quoi que ce soit.",
              "attention": "Il faut d’abord qu’un autre prenne le relais : invitez quelqu’un comme parent administrateur. Le rôle d’une personne qui a déjà un compte ne se change pas. C’est une protection contre l’enfermement, pas un obstacle arbitraire."
            },
            {
              "quoi": "Vos projets ne s’effacent pas tout seuls",
              "comment": "Un projet dont vous êtes propriétaire ne disparaît pas avec votre compte. Décidez avant de partir : l’archiver, ou le supprimer."
            }
          ]
        }
      ]
    },
    {
      "id": "limites",
      "titre": "Ce que Clannik ne fait pas",
      "resume": "Les manques assumés, dits franchement plutôt que découverts en route.",
      "sections": [
        {
          "titre": "Pas encore branché",
          "paragraphes": [
            "Cette notice ne décrit que ce qui marche pour de bon. Une chose reste en attente."
          ],
          "points": [
            {
              "quoi": "La recherche sur le web",
              "comment": "Prévue, mais elle dépend d’un fournisseur extérieur qui n’est pas encore raccordé."
            }
          ]
        },
        {
          "titre": "Choisi, et qui ne changera pas",
          "paragraphes": ["Ces absences ne sont pas des retards : ce sont des décisions."],
          "points": [
            {
              "quoi": "Aucune inscription libre",
              "comment": "On n’entre dans Clannik que par invitation. Il n’y aura pas de page d’inscription ouverte."
            },
            {
              "quoi": "Pas de transfert de propriété d’un projet",
              "comment": "Le propriétaire est celui qui a créé le projet, définitivement. Pour changer de main, il faut refaire un projet."
            },
            {
              "quoi": "Aucun contenu pédagogique embarqué",
              "comment": "Clannik n’enseigne pas l’usage de l’IA. C’est un atelier, pas un cours."
            },
            {
              "quoi": "Rien n’est partagé hors de la famille",
              "comment": "Il n’existe aucun lien public, aucun partage vers l’extérieur, aucune publication."
            }
          ]
        },
        {
          "titre": "Ce que cette notice ne remplace pas",
          "paragraphes": [
            "Elle décrit l’usage. Elle ne dit ni comment Clannik est construit, ni comment il est installé, ni ce qu’il fait pour se protéger — ces choses vivent dans les documents du projet, à côté du code."
          ]
        }
      ]
    }
  ]
}
